import { buildApiUrl } from "@/config/api";
import { API_FETCH_CACHE_MODE, getApiErrorMessage, parseApiResponse, prepareRequestBody, type JsonRequestBody } from "@/shared/http/request";

type PublicRequestOptions = Omit<RequestInit, "body"> & {
  body?: JsonRequestBody;
};

const communityStatsRetryDelaysMs = [0, 450, 1200, 2500] as const;

interface PublicUserProfile {
  sports: unknown[];
  statutAdhesion: string;
  cotisation: string;
  points: number;
  rank: string;
  nextRank: string;
  pointsToNext: number;
}

export interface PublicAuthUser {
  id: string;
  email: string | null;
  matricule: string | null;
  mustChangePassword: boolean;
  prenom: string;
  nom: string;
  telephone: string | null;
  departement: string | null;
  role: string;
  statut: string;
  avatar: string | null;
  profil: PublicUserProfile | null;
}

export interface RegisterMemberInput {
  firstName: string;
  lastName: string;
  email: string;
  matricule?: string | null;
  password: string;
  phone?: string;
  department?: string;
  avatarUrl?: string;
  sports?: string[];
  interests?: string[];
  acceptMonthlyContribution: boolean;
  acceptTerms: boolean;
  legalVersion: string;
}

export interface LoginMemberInput {
  identifier: string;
  password: string;
  acceptTerms: boolean;
  legalVersion: string;
}

export interface ChangeMemberPasswordInput {
  currentPassword?: string;
  newPassword: string;
}

export interface ForgotMemberPasswordInput {
  phone: string;
  email: string;
}

export interface UpdateMemberProfileInput {
  firstName: string;
  lastName: string;
  email?: string | null;
}

export interface PublicDirectionItem {
  id: string;
  nom: string;
}

export interface ContactMessageInput {
  fullName: string;
  email: string;
  subject: string;
  message: string;
}

export interface RegisterMemberResponse {
  user: PublicAuthUser;
}

export interface LoginMemberResponse {
  accessToken: string;
  expiresIn: string;
  user: PublicAuthUser;
}

export interface RefreshMemberResponse {
  accessToken: string;
  expiresIn: string;
  user: PublicAuthUser;
}

export interface ChangeMemberPasswordResponse {
  accessToken: string;
  expiresIn: string;
  user: PublicAuthUser;
}

export interface PublicNewsItem {
  id: string;
  titre: string;
  categorie: string;
  date: string;
  extrait: string;
  image: string | null;
  contenu: string;
  featured: boolean;
  statut: "DRAFT" | "PUBLISHED" | "ARCHIVED";
}

export interface PublicEventItem {
  id: string;
  titre: string;
  type: "MATCH" | "TRAINING" | "TOURNAMENT" | "CULTURE" | "BUREAU_MEETING" | "GENERAL";
  date: string;
  heure: string;
  dateFin: string | null;
  heureFin: string | null;
  lieu: string;
  participants: number;
  maxParticipants: number;
  description: string;
  image: string | null;
  meetingUrl: string | null;
  meetingParticipantIds?: string[];
  statut: "PUBLISHED" | "CANCELLED" | "COMPLETED";
}

export interface FamilyEventRegistrationResult {
  event: PublicEventItem;
  parentDepartment: string;
  children: string[];
}

export interface PublicProductItem {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string | null;
  category: string;
  stock: number;
  badge?: string | null;
  isActive: boolean;
}

export interface PublicGalleryItem {
  id: string;
  title: string;
  src: string;
  category: string;
  mediaType: "image" | "video";
}

export interface PublicClubTrophy {
  id: string;
  titre: string;
  sport: string;
  annee: string;
  rang: string;
  image: string;
  ordre: number;
}

export interface PublicClubPalmares {
  id: string;
  annee: string;
  titre: string;
  sport: string;
  rang: string;
  ordre: number;
}

export interface PublicLeisureVideo {
  id: string;
  title: string;
  sourceUrl: string;
  embedUrl: string;
  provider: string;
  videoId: string;
  thumbnailUrl: string;
  isLive: boolean;
  createdAt: string;
}

export interface PublicBureauMember {
  id: string;
  nom: string;
  role: string;
  departement: string | null;
  email: string | null;
  telephone: string | null;
  avatar: string | null;
  ordre: number;
}

export interface PublicDocumentItem {
  id: string;
  titre: string;
  categorie: string;
  date: string;
  taille: string;
  type: string;
  url: string | null;
}

export interface PublicScheduleItem {
  id: string;
  jour: string;
  activite: string;
  heure: string;
  lieu: string;
  categorie: string;
}

export interface PublicCommunityStats {
  activeMembers: number;
  sportsPracticed: number;
  activeTeams: number;
  privateGroups: number;
  activeChallenges: number;
}

export interface PublicMemberPointsBreakdown {
  steps: number;
  events: number;
  challengesJoined: number;
  challengesCompleted: number;
  squadsJoined: number;
  teamsJoined: number;
  squadsCreated: number;
  orders: number;
}

export interface PublicMemberPointsLeaderboardItem {
  userId: string;
  prenom: string;
  nom: string;
  name: string;
  avatar: string | null;
  position: number;
  totalSteps: number;
  points: number;
  rank: string;
  nextRank: string;
  pointsToNext: number;
  isCurrentUser: boolean;
  breakdown: PublicMemberPointsBreakdown;
  counters: {
    eventsAttended: number;
    challengesDone: number;
    squadsJoined: number;
    teamsJoined: number;
    approvedSquadsCreated: number;
    paidOrders: number;
  };
}

export interface PublicSquadMember {
  id: string;
  name: string;
  avatar: string | null;
  online: boolean;
}

export interface PublicSquadItem {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  color: string;
  maxMembers: number;
  status: "OPEN" | "FULL" | "CLOSED";
  members: PublicSquadMember[];
}

export interface PublicTeamPlayer {
  id: string;
  nom: string;
  poste: string;
  numero: number;
  userId?: string | null;
}

export interface PublicTeamItem {
  id: string;
  nom: string;
  sport: string;
  couleur: string;
  entraineur: string;
  statut: "actif" | "inactif";
  logo?: string | null;
  isMember?: boolean;
  joinRequestStatus?: "PENDING" | "APPROVED" | "REFUSED" | null;
  joueurs: PublicTeamPlayer[];
}

export interface PublicTrainingItem {
  id: string;
  equipeId: string;
  equipeNom: string;
  sport: string;
  jour: string;
  heure: string;
  lieu: string;
}

export interface PublicMatchItem {
  id: string;
  equipeDomicile: string;
  equipeExterieur: string;
  sport: string;
  date: string;
  heure: string;
  lieu: string;
  type: "FRIENDLY" | "LEAGUE" | "CUP" | "TOURNAMENT";
  statut: "SCHEDULED" | "LIVE" | "COMPLETED" | "CANCELLED";
  scoreDomicile?: number | null;
  scoreExterieur?: number | null;
}

export interface CreateSquadInput {
  name: string;
  sport: string;
  description: string;
}

export interface PublicChallengeLeaderboardEntry {
  rank: number;
  name: string;
  avatar: string | null;
  score: number;
  badge?: string;
}

export interface PublicChallengeItem {
  id: string;
  title: string;
  description: string;
  sport: string;
  icon: string;
  color: string;
  bgColor: string;
  image: string | null;
  target: number;
  unit: string;
  deadline: string;
  participants: number;
  myScore: number;
  myRank: number;
  reward: string;
  creatorName: string | null;
  createdByMe: boolean;
  createdBySquadLeader: boolean;
  leaderboard: PublicChallengeLeaderboardEntry[];
}

export interface CreateMemberChallengeInput {
  title: string;
  description: string;
  sport: string;
  image?: string | null;
  target: number;
  unit: string;
  reward: string;
  deadline: string;
}

export interface PublicForumCommentItem {
  id: string;
  author: {
    name: string;
    avatar: string | null;
  };
  content: string;
  audioUrl?: string | null;
  audioDurationSeconds?: number | null;
  timestamp: string;
  likes: number;
}

export interface PublicForumPostItem {
  id: string;
  author: {
    id: string;
    name: string;
    avatar: string | null;
    role: string;
  };
  category: string;
  title: string;
  content: string;
  image?: string | null;
  audioUrl?: string | null;
  audioDurationSeconds?: number | null;
  likes: number;
  isLiked: boolean;
  timestamp: string;
  comments: PublicForumCommentItem[];
}

export interface PublicSocialCommentItem {
  id: string;
  author: {
    name: string;
    avatar: string | null;
  };
  content: string;
  timestamp: string;
  likes: number;
}

export interface PublicSocialPostItem {
  id: string;
  author: {
    id: string;
    name: string;
    avatar: string | null;
    squad: string;
    role: string;
  };
  content: string;
  image?: string | null;
  likes: number;
  isLiked: boolean;
  comments: PublicSocialCommentItem[];
  timestamp: string;
  type: string;
  tags?: string[];
}

export interface CreateForumPostInput {
  category: string;
  title: string;
  content?: string;
  image?: string;
  audioUrl?: string;
  audioDurationSeconds?: number;
}

export interface CreateSocialPostInput {
  type?: string;
  content: string;
  image?: string;
  tags?: string[];
}

export interface PrivateDiscussionThread {
  id: string;
  scope: "SQUAD" | "TEAM";
  entityId: string;
  name: string;
  color: string;
  icon: string;
  sport: string;
  lastMessage: string;
  lastMessageTime: string;
  unread: number;
  members: Array<{
    id: string | null;
    name: string;
    avatar: string | null;
    online: boolean;
  }>;
  messages: Array<{
    id: string;
    senderId: string;
    senderName: string;
    senderAvatar: string | null;
    content: string;
    timestamp: string;
    isMe: boolean;
    type: "TEXT" | "IMAGE" | "AUDIO";
    imageUrl?: string | null;
    audioUrl?: string | null;
    audioDurationSeconds?: number | null;
    readByOthersCount: number;
    isReadByAllMembers: boolean;
  }>;
}

export interface CreateDiscussionMessageInput {
  type?: "TEXT" | "IMAGE" | "AUDIO";
  content?: string;
  imageUrl?: string;
  imagePublicId?: string;
  imageResourceType?: string;
  audioUrl?: string;
  audioPublicId?: string;
  audioResourceType?: string;
  audioDurationSeconds?: number;
}

export interface PublicLiveStreamItem {
  id: string;
  title: string;
  badgeLabel: string;
  embedUrl: string;
  provider: string;
  isActive: boolean;
  startedAt: string;
  endedAt: string | null;
}

export type OrderStatus = "PENDING" | "CONFIRMED" | "SHIPPED" | "DELIVERED" | "CANCELLED";
export type PaymentStatus = "PENDING" | "PROCESSING" | "SUCCEEDED" | "FAILED";

export interface MemberOrderItem {
  id: string;
  code: string;
  userId: string | null;
  memberName: string;
  memberEmail: string | null;
  status: OrderStatus;
  paymentProvider: string | null;
  paymentStatus: PaymentStatus;
  paymentReference: string | null;
  paymentPaidAt: string | null;
  total: number;
  date: string;
  items: Array<{
    productId: string | null;
    name: string;
    description: string | null;
    imageUrl: string | null;
    category: string | null;
    badge: string | null;
    quantity: number;
    unitPrice: number;
  }>;
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateMemberOrderInput {
  memberName: string;
  memberEmail: string | null;
  items: Array<{
    productId: string;
    quantity: number;
  }>;
}

export interface MemberDashboard {
  profil: {
    id: string;
    nom: string;
    prenom: string;
    email: string | null;
    matricule: string | null;
    telephone: string | null;
    departement: string | null;
    sport: string[];
    statut: string;
    dateInscription: string;
    dateValidation?: string;
    avatar: string | null;
    cotisation: string;
  } | null;
  statistiques: {
    points: number;
    rank: string;
    nextRank: string;
    pointsToNext: number;
    availableEvents: number;
    eventsAttended: number;
    challengesCompleted: number;
    squadsJoined: number;
    teamsJoined: number;
  } | null;
  permissions: {
    canCreateChallenges: boolean;
    approvedSquadsCreated: number;
  };
  upcomingEvents: Array<{
    id: string;
    titre: string;
    type: string;
    date: string;
    heure: string;
    dateFin: string | null;
    heureFin: string | null;
    lieu: string;
    meetingUrl: string | null;
  }>;
  latestNews: Array<{
    id: string;
    titre: string;
    categorie: string;
    date: string;
  }>;
}

export interface MemberNotificationItem {
  id: string;
  type: string;
  title: string;
  body: string;
  linkUrl: string | null;
  avatarUrl: string | null;
  read: boolean;
  createdAt: string;
}

export interface MemberPushConfiguration {
  publicKey: string;
  enabled: boolean;
  preferenceEnabled: boolean;
  subscriptionCount: number;
}

export interface MemberPushSubscriptionInput {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export class PublicApiError extends Error {
  public readonly status: number;
  public readonly details?: unknown;

  public constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = "PublicApiError";
    this.status = status;
    this.details = details;
  }
}

async function requestPublic<T>(path: string, options: PublicRequestOptions): Promise<T> {
  const { requestBody, serializeJsonBody } = prepareRequestBody(options.body);

  const response = await fetch(buildApiUrl(path), {
    ...options,
    cache: API_FETCH_CACHE_MODE,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(serializeJsonBody ? { "Content-Type": "application/json" } : {}),
      ...(options.headers ?? {})
    },
    body: requestBody
  });

  const payload = await parseApiResponse<T>(response);

  if (!response.ok) {
    throw new PublicApiError(getApiErrorMessage(payload), response.status, payload);
  }

  if (!payload) {
    return undefined as T;
  }

  return payload.data;
}

function waitForPublicRetry(delayMs: number) {
  return new Promise<void>((resolve) => {
    globalThis.setTimeout(resolve, delayMs);
  });
}

export async function registerMember(input: RegisterMemberInput) {
  return requestPublic<RegisterMemberResponse>("/auth/register", {
    method: "POST",
    body: input
  });
}

export async function sendContactMessage(input: ContactMessageInput) {
  return requestPublic<{ id: string; status: string }>("/contact-messages", {
    method: "POST",
    body: input
  });
}

export interface UploadedPublicMediaAsset {
  url: string;
  publicId: string;
  resourceType: string;
  format: string | null;
  bytes: number;
  width: number | null;
  height: number | null;
  duration: number | null;
  originalFilename: string;
}

export async function uploadPublicProfileImage(file: File) {
  const formData = new FormData();
  formData.append("file", file);

  return requestPublic<UploadedPublicMediaAsset>("/media/public-profile-image", {
    method: "POST",
    body: formData
  });
}

export async function uploadCommunityImage(accessToken: string, file: File) {
  const formData = new FormData();
  formData.append("file", file);

  return requestPublic<UploadedPublicMediaAsset>("/media/community-image", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: formData
  });
}

export async function uploadCommunityAudio(accessToken: string, file: File) {
  const formData = new FormData();
  formData.append("file", file);

  return requestPublic<UploadedPublicMediaAsset>("/media/community-audio", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: formData
  });
}

export async function loginMember(input: LoginMemberInput) {
  return requestPublic<LoginMemberResponse>("/auth/login", {
    method: "POST",
    body: input
  });
}

export async function requestMemberPasswordReset(input: ForgotMemberPasswordInput) {
  return requestPublic<null>("/auth/forgot-password", {
    method: "POST",
    body: input
  });
}

export async function refreshMemberSession() {
  return requestPublic<RefreshMemberResponse>("/auth/refresh", {
    method: "POST",
    body: {}
  });
}

export async function logoutMemberSession() {
  return requestPublic<null>("/auth/logout", {
    method: "POST",
    body: {}
  });
}

export async function changeMemberPassword(accessToken: string, input: ChangeMemberPasswordInput) {
  return requestPublic<ChangeMemberPasswordResponse>("/auth/change-password", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: input
  });
}

export async function updateMemberAvatar(accessToken: string, avatarUrl: string) {
  return requestPublic<PublicAuthUser>("/auth/me/avatar", {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: { avatarUrl }
  });
}

export async function fetchMemberProfile(accessToken: string) {
  return requestPublic<PublicAuthUser>("/auth/me", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });
}

export async function updateMemberProfile(accessToken: string, input: UpdateMemberProfileInput) {
  return requestPublic<PublicAuthUser>("/auth/me/profile", {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: input
  });
}

export async function fetchMemberDashboard(accessToken: string) {
  return requestPublic<MemberDashboard>("/members/member-dashboard", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });
}

export async function fetchMemberNotifications(accessToken: string) {
  return requestPublic<MemberNotificationItem[]>("/notifications", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });
}

export async function fetchMemberPushConfiguration(accessToken: string) {
  return requestPublic<MemberPushConfiguration>("/notifications/push-config", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });
}

export async function saveMemberPushSubscription(accessToken: string, input: MemberPushSubscriptionInput) {
  return requestPublic<null>("/notifications/push-subscriptions", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: input
  });
}

export async function deleteMemberPushSubscription(accessToken: string, endpoint?: string | null) {
  return requestPublic<null>("/notifications/push-subscriptions", {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: endpoint ? { endpoint } : {}
  });
}

export async function markMemberNotificationRead(accessToken: string, notificationId: string) {
  return requestPublic<null>(`/notifications/${notificationId}/read`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: {}
  });
}

export async function markAllMemberNotificationsRead(accessToken: string) {
  return requestPublic<null>("/notifications/read-all", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: {}
  });
}

export async function fetchMemberOrders(accessToken: string, params?: { page?: number; limit?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.limit) searchParams.set("limit", String(params.limit));

  const query = searchParams.toString();

  return requestPublic<PaginatedResult<MemberOrderItem>>(`/orders/me${query ? `?${query}` : ""}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });
}

export async function fetchShopProducts(params?: { category?: string; search?: string; page?: number; limit?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.category) searchParams.set("category", params.category);
  if (params?.search) searchParams.set("search", params.search);
  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.limit) searchParams.set("limit", String(params.limit));

  return requestPublic<PaginatedResult<PublicProductItem>>(`/products${searchParams.size > 0 ? `?${searchParams.toString()}` : ""}`, {
    method: "GET"
  });
}

export async function fetchShopProductById(id: string) {
  return requestPublic<PublicProductItem>(`/products/${id}`, {
    method: "GET"
  });
}

export async function createMemberOrder(accessToken: string, input: CreateMemberOrderInput) {
  return requestPublic<MemberOrderItem>("/orders", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: input
  });
}

export async function fetchPublicNews(params: { search?: string; category?: string; page?: number; limit?: number }) {
  const searchParams = new URLSearchParams();
  searchParams.set("status", "PUBLISHED");
  if (params.search) searchParams.set("search", params.search);
  if (params.category) searchParams.set("category", params.category);
  if (params.page) searchParams.set("page", String(params.page));
  if (params.limit) searchParams.set("limit", String(params.limit));

  return requestPublic<PaginatedResult<PublicNewsItem>>(`/news?${searchParams.toString()}`, {
    method: "GET"
  });
}

export async function fetchPublicNewsById(id: string) {
  return requestPublic<PublicNewsItem>(`/news/${id}`, {
    method: "GET"
  });
}

export async function fetchPublicEvents(params?: {
  status?: PublicEventItem["statut"];
  type?: PublicEventItem["type"];
  page?: number;
  limit?: number;
}) {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set("status", params.status);
  if (params?.type) searchParams.set("type", params.type);
  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.limit) searchParams.set("limit", String(params.limit));

  return requestPublic<PaginatedResult<PublicEventItem>>(`/events${searchParams.size > 0 ? `?${searchParams.toString()}` : ""}`, {
    method: "GET"
  });
}

export async function fetchDirections() {
  return requestPublic<PublicDirectionItem[]>("/members/directions", {
    method: "GET"
  });
}

export async function registerMemberForEvent(accessToken: string, eventId: string, goal?: string) {
  return requestPublic<PublicEventItem>(`/events/${eventId}/registrations`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: goal?.trim()
      ? {
          goal: goal.trim()
        }
      : {}
  });
}

export async function registerChildrenForFamilyEvent(accessToken: string, eventId: string, childNames: string[]) {
  return requestPublic<FamilyEventRegistrationResult>(`/events/${eventId}/family-registrations`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: {
      childNames
    }
  });
}

export async function fetchPublicGallery() {
  return requestPublic<PublicGalleryItem[]>("/culture/gallery", {
    method: "GET"
  });
}

export async function fetchPublicClubTrophies() {
  return requestPublic<PublicClubTrophy[]>("/club/trophies", {
    method: "GET"
  });
}

export async function fetchPublicClubPalmares() {
  return requestPublic<PublicClubPalmares[]>("/club/palmares", {
    method: "GET"
  });
}

export async function fetchPublicLeisureVideos() {
  return requestPublic<PublicLeisureVideo[]>("/culture/leisure-videos", {
    method: "GET"
  });
}

export async function fetchPublicBureau() {
  return requestPublic<PublicBureauMember[]>("/members/bureau", {
    method: "GET"
  });
}

export async function fetchPublicDocuments() {
  return requestPublic<PublicDocumentItem[]>("/members/documents", {
    method: "GET"
  });
}

export async function fetchPublicSchedules() {
  return requestPublic<PublicScheduleItem[]>("/members/schedules", {
    method: "GET"
  });
}

export async function fetchPublicCommunityStats() {
  let lastError: unknown;

  for (const delayMs of communityStatsRetryDelaysMs) {
    if (delayMs > 0) {
      await waitForPublicRetry(delayMs);
    }

    try {
      return await requestPublic<PublicCommunityStats>("/members/public-stats", {
        method: "GET"
      });
    } catch (caughtError) {
      lastError = caughtError;
    }
  }

  throw lastError instanceof Error ? lastError : new Error("Impossible de charger les statistiques publiques.");
}

export async function fetchPublicMemberPointsLeaderboard() {
  return requestPublic<PublicMemberPointsLeaderboardItem[]>("/members/points-leaderboard", {
    method: "GET"
  });
}

export async function fetchMemberPointsLeaderboard(accessToken: string) {
  return requestPublic<PublicMemberPointsLeaderboardItem[]>("/members/me/points-leaderboard", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });
}

export async function fetchCommunitySquads() {
  return requestPublic<PublicSquadItem[]>("/squads", {
    method: "GET"
  });
}

export async function fetchCommunityTeams(accessToken?: string | null) {
  const data = await requestPublic<Array<{
    id: string;
    nom: string;
    sport: string;
    couleur: string;
    entraineur: string;
    statut: string;
    logo?: string | null;
    isMember?: boolean;
    joinRequestStatus?: "PENDING" | "APPROVED" | "REFUSED" | null;
    joueurs: Array<{
      id: string;
      nom: string;
      poste: string;
      numero: number;
      userId?: string | null;
    }>;
  }>>("/teams", {
    method: "GET",
    ...(accessToken
      ? {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      : {})
  });

  return data.map((team) => ({
    ...team,
    statut: team.statut === "ACTIVE" ? "actif" : "inactif"
  })) as PublicTeamItem[];
}

export async function requestTeamJoin(accessToken: string, teamId: string) {
  return requestPublic(`/teams/${teamId}/join-requests`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: {}
  });
}

export async function fetchPublicMatches() {
  return requestPublic<PublicMatchItem[]>("/matches", {
    method: "GET"
  });
}

export async function fetchPublicTrainings() {
  return requestPublic<PublicTrainingItem[]>("/trainings", {
    method: "GET"
  });
}

export async function createCommunitySquad(accessToken: string, input: CreateSquadInput) {
  return requestPublic("/squads", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: input
  });
}

export async function joinCommunitySquad(accessToken: string, squadId: string) {
  return requestPublic<PublicSquadItem>(`/squads/${squadId}/join`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: {}
  });
}

export async function fetchCommunityForumPosts(options?: { category?: string; accessToken?: string }) {
  const searchParams = new URLSearchParams();
  if (options?.category) {
    searchParams.set("category", options.category);
  }

  const query = searchParams.toString();

  return requestPublic<PublicForumPostItem[]>(`/forum/posts${query ? `?${query}` : ""}`, {
    method: "GET",
    headers: options?.accessToken
      ? {
          Authorization: `Bearer ${options.accessToken}`
        }
      : undefined
  });
}

export async function fetchCommunitySocialPosts(options?: { accessToken?: string }) {
  return requestPublic<PublicSocialPostItem[]>("/social/posts", {
    method: "GET",
    headers: options?.accessToken
      ? {
          Authorization: `Bearer ${options.accessToken}`
        }
      : undefined
  });
}

export async function createCommunitySocialPost(accessToken: string, input: CreateSocialPostInput) {
  return requestPublic<PublicSocialPostItem>("/social/posts", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: input
  });
}

export async function addCommunitySocialComment(accessToken: string, postId: string, content: string) {
  return requestPublic<PublicSocialPostItem>(`/social/posts/${postId}/comments`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: {
      content
    }
  });
}

export async function toggleCommunitySocialLike(accessToken: string, postId: string) {
  return requestPublic<PublicSocialPostItem>(`/social/posts/${postId}/like`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: {}
  });
}

export async function createCommunityForumPost(accessToken: string, input: CreateForumPostInput) {
  return requestPublic<PublicForumPostItem>("/forum/posts", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: input
  });
}

export async function addCommunityForumComment(accessToken: string, postId: string, input: { content?: string; audioUrl?: string; audioDurationSeconds?: number }) {
  return requestPublic<PublicForumPostItem>(`/forum/posts/${postId}/comments`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: input
  });
}

export async function toggleCommunityForumLike(accessToken: string, postId: string) {
  return requestPublic<PublicForumPostItem>(`/forum/posts/${postId}/like`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: {}
  });
}

export async function fetchPrivateDiscussionThreads(accessToken: string) {
  return requestPublic<PrivateDiscussionThread[]>("/social/chats", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });
}

export async function sendPrivateDiscussionMessage(accessToken: string, chatId: string, input: CreateDiscussionMessageInput) {
  return requestPublic<PrivateDiscussionThread>(`/social/chats/${chatId}/messages`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: input
  });
}

export async function deletePrivateDiscussionMessage(accessToken: string, chatId: string, messageId: string) {
  return requestPublic<PrivateDiscussionThread>(`/social/chats/${chatId}/messages/${messageId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });
}

export async function markPrivateDiscussionRead(accessToken: string, chatId: string) {
  return requestPublic<PrivateDiscussionThread>(`/social/chats/${chatId}/read`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: {}
  });
}

export async function fetchActiveLiveStream() {
  return requestPublic<PublicLiveStreamItem | null>("/live-streams/active", {
    method: "GET"
  });
}

export async function fetchMemberChallenges(accessToken: string) {
  return fetchPublicChallenges({ accessToken });
}

export async function fetchPublicChallenges(options?: { accessToken?: string }) {
  return requestPublic<PublicChallengeItem[]>("/challenges", {
    method: "GET",
    headers: options?.accessToken
      ? {
          Authorization: `Bearer ${options.accessToken}`
        }
      : undefined
  });
}

export async function createMemberChallenge(accessToken: string, input: CreateMemberChallengeInput) {
  return requestPublic<PublicChallengeItem>("/challenges", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: input
  });
}

export async function updateMemberChallenge(accessToken: string, challengeId: string, input: CreateMemberChallengeInput) {
  return requestPublic<PublicChallengeItem>(`/challenges/${challengeId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: input
  });
}

export async function deleteMemberChallenge(accessToken: string, challengeId: string) {
  return requestPublic<null>(`/challenges/${challengeId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });
}

export async function joinMemberChallenge(accessToken: string, challengeId: string) {
  return requestPublic<PublicChallengeItem>(`/challenges/${challengeId}/join`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: {}
  });
}
